import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { BookOpen, Clock, CheckCircle, PlayCircle, Award, Trophy, TrendingUp } from 'lucide-react';

const STAT_ITEMS = [
  { key: 'total', label: 'Total Assigned', icon: BookOpen, tone: 'bg-primary/10 text-primary' },
  { key: 'in_progress', label: 'In Progress', icon: TrendingUp, tone: 'bg-blue-500/10 text-blue-500' },
  { key: 'completed', label: 'Completed', icon: CheckCircle, tone: 'bg-emerald-500/10 text-emerald-500' },
  { key: 'not_started', label: 'Not Started', icon: Trophy, tone: 'bg-amber-500/10 text-amber-500' }
];

export const ModuleCourseStats = ({ stats }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {STAT_ITEMS.map(({ key, label, icon: Icon, tone }) => (
      <Card key={key}>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${tone}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-2xl font-bold" data-testid={`module-stat-${key}`}>{stats[key] || 0}</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

const STATUS_STYLES = {
  completed: { label: 'Completed', className: 'bg-emerald-500' },
  in_progress: { label: 'In Progress', className: 'bg-blue-500' },
  not_started: { label: 'Not Started', className: 'bg-secondary text-secondary-foreground' }
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status];
  if (!style) return <Badge variant="outline">{status}</Badge>;
  return <Badge className={style.className}>{style.label}</Badge>;
};

export const ModuleCourseCard = ({ course, to }) => {
  const progress = course.user_progress || {};
  const status = progress.status || 'not_started';
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`module-course-card-${course.id}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg truncate">{course.title}</CardTitle>
        <CardDescription className="line-clamp-2 mt-1">{course.description || 'No description'}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-2 mb-3">
          <StatusBadge status={status} />
          {course.category_name && <Badge variant="outline" className="text-xs">{course.category_name}</Badge>}
          {progress.certificate_id && (
            <Badge className="bg-amber-500 text-xs"><Award className="h-3 w-3 mr-1" />Certified</Badge>
          )}
        </div>
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{progress.overall_progress_percent || 0}%</span>
          </div>
          <Progress value={progress.overall_progress_percent || 0} className="h-2" />
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-4">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{course.estimated_hours || 0}h</span>
          <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" />{course.module_count || 0} modules</span>
          {progress.overall_score > 0 && (
            <span className="flex items-center gap-1"><Award className="h-3 w-3" />{progress.overall_score}%</span>
          )}
        </div>
        {progress.certificate_id && (
          <Button asChild className="w-full mb-2" variant="outline">
            <Link to={`${to}/certificate`} data-testid={`module-course-certificate-${course.id}`}>
              <Award className="h-4 w-4 mr-2" />View Certificate
            </Link>
          </Button>
        )}
        <Button asChild className="w-full" variant={status === 'completed' ? 'outline' : 'default'}>
          <Link to={to} data-testid={`module-course-open-${course.id}`}>
            <PlayCircle className="h-4 w-4 mr-2" />
            {status === 'completed' ? 'Review Course' : status === 'in_progress' ? 'Continue Learning' : 'Start Course'}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
};
